import { apiClient, newIdempotencyKey } from './client'
import { ApiError } from './problem'
import type { CursorPage } from './accounts'
import type {
  ArtifactTransitionRequest,
  CalcFixtureRunSummaryView,
  CalcTestFixtureView,
  CalculationGraphView,
  ClonedProgramView,
  CloneProgramRequest,
  CreateCalcTestFixtureRequest,
  CreateCalculationGraphRequest,
  CreateDecisionRequest,
  CreateDecisionRuleRequest,
  CreateEligibilityModelRequest,
  CreateJudgmentGateRequest,
  CreateProgramRequest,
  CreateTestFixtureRequest,
  DecisionRuleView,
  DecisionView,
  EligibilityModelView,
  FixtureRunSummaryView,
  JudgmentGateView,
  ParameterView,
  ProgramView,
  PublishParameterRequest,
  PublishRuleSetRequest,
  RuleSetView,
  TestFixtureView,
} from '../types/cartridgeStudio'

/**
 * IB-210 Slice 2 (Cartridge Authoring Studio UI, demo v66) — every call below binds to a real,
 * merged endpoint (see `docs/design/IB-210-cartridge-studio-ui-design.md` and
 * `docs/stories/IB-210-cartridge-studio-ui-DoR.md` §1 for the traceability). No endpoint here is
 * invented; `SimulationPanel`/AI-draft/tenant-overlay surfaces are deliberately absent (DoR
 * findings #1/§2 — no backend route exists for them). `cloneProgram` (IB-285/IB-211 S2) was added
 * once its backend (IB-284/IB-211 S1, merged) landed a real route.
 *
 * Idempotency-Key: the backend's `IdempotencyFilterConfig` currently registers the
 * `Idempotency-Key` requirement only against `/api/v1/programs`, `/api/v1/programs/*`, and
 * `/api/v1/rule-sets/*` (so `POST /programs`, `.../rule-sets(-drafts)`, `.../parameters
 * (-drafts)`, and RuleSet's own `review-submissions`/`approvals`/`publications` DO require it —
 * `Parameter`'s equivalent transition routes, at `/api/v1/parameters/*`, and every
 * eligibility/calc route do NOT, per IB-52's still-open platform-wide widening). Sending a fresh
 * key on every creating POST here regardless is harmless where not required and correct where it
 * is — simpler and safer than tracking the exact allow-list per call site.
 */

// ---------------------------------------------------------------------------
// module.cartridge — Program
// ---------------------------------------------------------------------------

export interface ListProgramsParams {
  filter?: string | null
  sort?: string | null
  cursor?: string | null
  limit?: number
}

function toQuery(params: Record<string, string | number | null | undefined>): string {
  const q = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') q.set(key, String(value))
  }
  return q.toString()
}

export async function listPrograms(params: ListProgramsParams, signal?: AbortSignal): Promise<CursorPage<ProgramView>> {
  const { data } = await apiClient.get<CursorPage<ProgramView>>(
    `/programs?${toQuery({ filter: params.filter, sort: params.sort, cursor: params.cursor, limit: params.limit ?? 50 })}`,
    { signal },
  )
  return data
}

export async function getProgram(id: string, signal?: AbortSignal): Promise<ProgramView> {
  const { data } = await apiClient.get<ProgramView>(`/programs/${id}`, { signal })
  return data
}

export async function createProgram(request: CreateProgramRequest): Promise<ProgramView> {
  const { data } = await apiClient.post<ProgramView>('/programs', request, { idempotencyKey: newIdempotencyKey() })
  return data
}

/**
 * `POST /programs/{sourceProgramId}/clones` (IB-284/IB-211 S1, ADR-0055, FR-M7.4) — template-clone.
 * Platform-only (canon authoring, ADR-0017): a real tenant caller gets a 403 before any read/write.
 * Deep-copies the source's currently-effective RuleSet + its published Parameters into NEW `draft`
 * artifacts under a brand-new Program — never a publish shortcut, never a second write path. The
 * `Idempotency-Key` requirement covers `/api/v1/programs/*` (see this file's header note), which
 * includes this route.
 */
export async function cloneProgram(sourceProgramId: string, request: CloneProgramRequest): Promise<ClonedProgramView> {
  const { data } = await apiClient.post<ClonedProgramView>(`/programs/${sourceProgramId}/clones`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

// ---------------------------------------------------------------------------
// module.cartridge — RuleSet
// ---------------------------------------------------------------------------

export async function listRuleSets(programId: string, signal?: AbortSignal): Promise<CursorPage<RuleSetView>> {
  const { data } = await apiClient.get<CursorPage<RuleSetView>>(`/programs/${programId}/rule-sets?limit=50`, { signal })
  return data
}

export async function getRuleSet(id: string, signal?: AbortSignal): Promise<RuleSetView> {
  const { data } = await apiClient.get<RuleSetView>(`/rule-sets/${id}`, { signal })
  return data
}

/** `POST /programs/{programId}/rule-sets` — creates `status=draft` ONLY (never a publish shortcut). */
export async function createRuleSetDraft(programId: string, request: PublishRuleSetRequest): Promise<RuleSetView> {
  const { data } = await apiClient.post<RuleSetView>(`/programs/${programId}/rule-sets`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

export async function submitRuleSetForReview(id: string, request: ArtifactTransitionRequest): Promise<RuleSetView> {
  const { data } = await apiClient.post<RuleSetView>(`/rule-sets/${id}/review-submissions`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

export async function approveRuleSet(id: string, request: ArtifactTransitionRequest): Promise<RuleSetView> {
  const { data } = await apiClient.post<RuleSetView>(`/rule-sets/${id}/approvals`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

export async function publishRuleSet(id: string, request: ArtifactTransitionRequest): Promise<RuleSetView> {
  const { data } = await apiClient.post<RuleSetView>(`/rule-sets/${id}/publications`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

// ---------------------------------------------------------------------------
// module.cartridge — Parameter
// ---------------------------------------------------------------------------

export async function listParameters(ruleSetId: string, signal?: AbortSignal): Promise<CursorPage<ParameterView>> {
  const { data } = await apiClient.get<CursorPage<ParameterView>>(`/rule-sets/${ruleSetId}/parameters?limit=200`, { signal })
  return data
}

/** `POST /rule-sets/{ruleSetId}/parameters` — creates `status=draft` ONLY, never a PATCH to a published row. */
export async function createParameterDraft(ruleSetId: string, request: PublishParameterRequest): Promise<ParameterView> {
  const { data } = await apiClient.post<ParameterView>(`/rule-sets/${ruleSetId}/parameters`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

export async function submitParameterForReview(id: string, request: ArtifactTransitionRequest): Promise<ParameterView> {
  const { data } = await apiClient.post<ParameterView>(`/parameters/${id}/review-submissions`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

export async function approveParameter(id: string, request: ArtifactTransitionRequest): Promise<ParameterView> {
  const { data } = await apiClient.post<ParameterView>(`/parameters/${id}/approvals`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

export async function publishParameter(id: string, request: ArtifactTransitionRequest): Promise<ParameterView> {
  const { data } = await apiClient.post<ParameterView>(`/parameters/${id}/publications`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

// ---------------------------------------------------------------------------
// module.eligibility — EligibilityModel
// ---------------------------------------------------------------------------

export async function createEligibilityModel(request: CreateEligibilityModelRequest): Promise<EligibilityModelView> {
  const { data } = await apiClient.post<EligibilityModelView>('/eligibility-models', request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

export async function getEligibilityModel(id: string, signal?: AbortSignal): Promise<EligibilityModelView> {
  const { data } = await apiClient.get<EligibilityModelView>(`/eligibility-models/${id}`, { signal })
  return data
}

export async function submitEligibilityModelForReview(
  id: string,
  request: ArtifactTransitionRequest,
): Promise<EligibilityModelView> {
  const { data } = await apiClient.post<EligibilityModelView>(`/eligibility-models/${id}/review-submissions`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

export async function approveEligibilityModel(id: string, request: ArtifactTransitionRequest): Promise<EligibilityModelView> {
  const { data } = await apiClient.post<EligibilityModelView>(`/eligibility-models/${id}/approvals`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

/** May 422 with a per-fixture `Violation` list — the real, server-enforced fixture gate (FR-M7.3). */
export async function publishEligibilityModel(id: string, request: ArtifactTransitionRequest): Promise<EligibilityModelView> {
  const { data } = await apiClient.post<EligibilityModelView>(`/eligibility-models/${id}/publications`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

export async function runEligibilityFixtures(id: string): Promise<FixtureRunSummaryView> {
  const { data } = await apiClient.post<FixtureRunSummaryView>(`/eligibility-models/${id}/fixture-runs`, undefined, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

// ---------------------------------------------------------------------------
// module.eligibility — Decision / DecisionRule / JudgmentGate / TestFixture
// ---------------------------------------------------------------------------

export async function listDecisions(modelId: string, signal?: AbortSignal): Promise<DecisionView[]> {
  const { data } = await apiClient.get<DecisionView[]>(`/eligibility-models/${modelId}/decisions`, { signal })
  return data
}

export async function createDecision(modelId: string, request: CreateDecisionRequest): Promise<DecisionView> {
  const { data } = await apiClient.post<DecisionView>(`/eligibility-models/${modelId}/decisions`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

export async function listDecisionRules(decisionId: string, signal?: AbortSignal): Promise<DecisionRuleView[]> {
  const { data } = await apiClient.get<DecisionRuleView[]>(`/decisions/${decisionId}/rules`, { signal })
  return data
}

export async function createDecisionRule(decisionId: string, request: CreateDecisionRuleRequest): Promise<DecisionRuleView> {
  const { data } = await apiClient.post<DecisionRuleView>(`/decisions/${decisionId}/rules`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

/** 404 (`GET`, since `JudgmentGateController.get` maps `EligibilityAuthoringService
 * .getJudgmentGateByDecision`, which returns 404 for "no gate yet") is treated as "none exists". */
export async function getJudgmentGateByDecision(decisionId: string, signal?: AbortSignal): Promise<JudgmentGateView | null> {
  try {
    const { data } = await apiClient.get<JudgmentGateView>(`/decisions/${decisionId}/judgment-gates`, { signal })
    return data
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null
    throw err
  }
}

/** 422 if the parent Decision is not `kind=gate`; 409 if a gate already exists (max one per Decision). */
export async function createJudgmentGate(decisionId: string, request: CreateJudgmentGateRequest): Promise<JudgmentGateView> {
  const { data } = await apiClient.post<JudgmentGateView>(`/decisions/${decisionId}/judgment-gates`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

export async function listTestFixtures(modelId: string, signal?: AbortSignal): Promise<TestFixtureView[]> {
  const { data } = await apiClient.get<TestFixtureView[]>(`/eligibility-models/${modelId}/test-fixtures`, { signal })
  return data
}

/** Cap 200/model, server-enforced (Security S3). */
export async function createTestFixture(modelId: string, request: CreateTestFixtureRequest): Promise<TestFixtureView> {
  const { data } = await apiClient.post<TestFixtureView>(`/eligibility-models/${modelId}/test-fixtures`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

// ---------------------------------------------------------------------------
// module.calc — CalculationGraph (ADR-0020, direct-publish, no maker-checker)
// ---------------------------------------------------------------------------

export async function listCalculationGraphs(programId: string, signal?: AbortSignal): Promise<CalculationGraphView[]> {
  const { data } = await apiClient.get<CalculationGraphView[]>(`/programs/${programId}/calculation-graphs`, { signal })
  return data
}

export async function getCalculationGraph(id: string, signal?: AbortSignal): Promise<CalculationGraphView> {
  const { data } = await apiClient.get<CalculationGraphView>(`/calculation-graphs/${id}`, { signal })
  return data
}

/** Immediately effective on create — no draft/review/approve step (ADR-0020). Succeeds even with a
 * red fixture on a prior version (AC5b's explicit negative check — not a regression to fix). */
export async function createCalculationGraph(
  programId: string,
  request: CreateCalculationGraphRequest,
): Promise<CalculationGraphView> {
  const { data } = await apiClient.post<CalculationGraphView>(`/programs/${programId}/calculation-graphs`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

// ---------------------------------------------------------------------------
// module.calc — CalcTestFixture
// ---------------------------------------------------------------------------

export async function listCalcTestFixtures(graphId: string, signal?: AbortSignal): Promise<CalcTestFixtureView[]> {
  const { data } = await apiClient.get<CalcTestFixtureView[]>(`/calculation-graphs/${graphId}/test-fixtures`, { signal })
  return data
}

/** Cap 200/graph, server-enforced. */
export async function createCalcTestFixture(graphId: string, request: CreateCalcTestFixtureRequest): Promise<CalcTestFixtureView> {
  const { data } = await apiClient.post<CalcTestFixtureView>(`/calculation-graphs/${graphId}/test-fixtures`, request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}

/** Informational only (RAID R-19) — never gates `createCalculationGraph`. */
export async function runCalcFixtures(graphId: string): Promise<CalcFixtureRunSummaryView> {
  const { data } = await apiClient.post<CalcFixtureRunSummaryView>(`/calculation-graphs/${graphId}/fixture-runs`, undefined, {
    idempotencyKey: newIdempotencyKey(),
  })
  return data
}
